"use client"
import { Avatar, Heading, SimpleGrid } from "@chakra-ui/react"
import { Text } from "@chakra-ui/react"
import { Box } from "@chakra-ui/react/box"
import { Link } from "@chakra-ui/react/link"
import { HStack, Stack } from "@chakra-ui/react/stack"
import { VStack } from "@chakra-ui/react/stack"
import dayjs, { type Dayjs } from "dayjs"
import { type FC } from "react"

import ArticleMetaActions from "@/components/ArticleMetaActions"
import CategoryBadgeListArticle from "@/components/CategoryBadgeListArticle"
import ClientZoom from "@/components/ClientZoom"
import { TagsIcon } from "@/components/icons"
import Img from "@/components/Img"
import Inner from "@/components/Inner"
import MarkdownParser from "@/components/MarkdownParser"
import NextLink from "@/components/RouteTransitionLink"
import StatsGrid from "@/components/StatsGrid"
import TextHighlighter from "@/components/TextHighlighter"
import { Author, Category, Image, PostStat, Skill } from "@/types/database"

const skillsURL = `${process.env.URL}/work?tags=`

interface PostClient {
  name?: string | null
  url?: string | null
  svg_logo?: string | null
}

export interface SingleHeaderProps {
  className?: string
  hiddenTLDR?: boolean
  title: string
  titleHighlight?: string | null
  tldr?: string
  read_time?: string
  skills: Skill[]
  thumbnail?: Image | null
  readingTime?: string
  categories: Category[]
  date: string
  headline?: string | null
  likeCount: number
  liked: boolean
  allow_comments: boolean | null | undefined
  commentCount: number
  articleSlug: string
  articlePathPrefix?: "/article" | "/case-study"
  displayNumber?: number
  author?: Author
  stats?: PostStat[] | null
  role?: string | null
  client?: PostClient | null
  startDate?: string | Date | null
  endDate?: string | Date | null
  industry?: string | null
  platform?: unknown
  team?: string | number | null
  methodsUsed?: string | null
  contentLocked?: boolean
}

const EMPTY_CATEGORIES: SingleHeaderProps["categories"] = []
const EMPTY_SKILLS: SingleHeaderProps["skills"] = []

const getKeyedItems = <T,>(
  items: T[],
  getBaseKey: (item: T) => string | null | undefined
) => {
  const counts = new Map<string, number>()

  return items.map((item) => {
    const baseKey = getBaseKey(item)?.trim() || "item"
    const count = (counts.get(baseKey) ?? 0) + 1
    counts.set(baseKey, count)

    return {
      item,
      key: count === 1 ? baseKey : `${baseKey}-${count}`,
    }
  })
}

const getStringValue = (value: unknown): string | undefined => {
  if (value === null || value === undefined) {
    return undefined
  }

  if (
    !(value instanceof Date) &&
    typeof value !== "string" &&
    typeof value !== "number"
  ) {
    return undefined
  }

  const stringValue =
    value instanceof Date ? dayjs(value).format("YYYY-MM-DD") : String(value)
  const trimmedValue = stringValue.trim()

  return trimmedValue.length > 0 ? trimmedValue : undefined
}

const getDate = (date: string | Date | null | undefined) => {
  if (!date) {
    return undefined
  }

  const parsedDate = dayjs(date)

  return parsedDate.isValid() ? parsedDate : undefined
}

const formatTimelineDuration = (startDate: Dayjs, endDate: Dayjs) => {
  const days = endDate.diff(startDate, "day") + 1

  if (days < 7) {
    return `${days} ${days === 1 ? "day" : "days"}`
  }

  if (days < 30) {
    const weeks = Math.max(Math.round(days / 7), 1)
    return `${weeks} ${weeks === 1 ? "week" : "weeks"}`
  }

  const monthCount =
    (endDate.year() - startDate.year()) * 12 +
    endDate.month() -
    startDate.month() +
    1
  const months = Math.max(monthCount, 1)

  return `${months} ${months === 1 ? "month" : "months"}`
}

const formatTimeline = (
  startDate: string | Date | null | undefined,
  endDate: string | Date | null | undefined
) => {
  const start = getDate(startDate)
  const end = getDate(endDate)

  if (start && end) {
    return `${start.format("MMM YYYY")} · ${formatTimelineDuration(start, end)}`
  }

  if (start) {
    return start.format("MMM YYYY")
  }

  if (end) {
    return end.format("MMM YYYY")
  }

  return undefined
}

const getMetadataLabel = (item: unknown): string | undefined => {
  const stringValue = getStringValue(item)

  if (stringValue) {
    return stringValue
  }

  if (item && typeof item === "object") {
    const record = item as Record<string, unknown>

    return (
      getStringValue(record.name) ??
      getStringValue(record.title) ??
      getStringValue(record.label) ??
      getStringValue(record.value) ??
      getStringValue(record.platform)
    )
  }

  return undefined
}

const getMetadataItems = (value: unknown): string[] => {
  if (value === null || value === undefined) {
    return []
  }

  if (Array.isArray(value)) {
    return value.flatMap(getMetadataItems)
  }

  if (typeof value === "string") {
    const trimmedValue = value.trim()

    if (!trimmedValue) {
      return []
    }

    try {
      const parsedValue = JSON.parse(trimmedValue) as unknown

      if (parsedValue !== trimmedValue) {
        return getMetadataItems(parsedValue)
      }
    } catch {
      // Fall back to comma/newline separated values from text-like Directus fields.
    }

    return trimmedValue.split(/[\n,]+/).flatMap((item) => {
      const label = item.trim()

      return label ? [label] : []
    })
  }

  const label = getMetadataLabel(value)

  if (label) {
    return [label]
  }

  if (typeof value === "object") {
    const record = value as Record<string, unknown>
    const nestedItems =
      record.items ??
      record.values ??
      record.platforms ??
      record.platform ??
      record.methods

    if (nestedItems !== undefined) {
      return getMetadataItems(nestedItems)
    }

    return Object.entries(record).flatMap(([key, recordValue]) => {
      if (recordValue === true) {
        return [key]
      }

      return getMetadataItems(recordValue)
    })
  }

  return []
}

const getUniqueItems = (items: string[]) => {
  const seen = new Set<string>()

  return items.filter((item) => {
    const key = item.toLowerCase()

    if (seen.has(key)) {
      return false
    }

    seen.add(key)

    return true
  })
}

const getHighlightedText = (text: string, highlight?: string | null) => {
  const normalizedHighlight = highlight?.trim()

  if (!normalizedHighlight) {
    return text
  }

  const highlightIndex = text
    .toLowerCase()
    .indexOf(normalizedHighlight.toLowerCase())

  if (highlightIndex === -1) {
    return text
  }

  return {
    after: text.slice(highlightIndex + normalizedHighlight.length),
    before: text.slice(0, highlightIndex),
    highlight: text.slice(
      highlightIndex,
      highlightIndex + normalizedHighlight.length
    ),
  }
}

const ProjectDetailItem = ({
  href,
  label,
  markdown = false,
  value,
}: {
  href?: string
  label: string
  value?: string
  markdown?: boolean
}) => {
  if (!value) {
    return null
  }

  return (
    <VStack alignItems="flex-start" gap={1}>
      <Text
        color="fg"
        fontFamily="mono"
        fontSize="xs"
        fontWeight="600"
        letterSpacing="0.12em"
        lineHeight="1.2"
        textTransform="uppercase"
      >
        {label}
      </Text>
      {href ? (
        <Link
          _hover={{ color: { base: "primary.500", _dark: "primary.300" } }}
          color="fg.muted"
          fontSize="sm"
          href={href}
          rel="noopener noreferrer"
          target="_blank"
        >
          {value}
        </Link>
      ) : markdown ? (
        <MarkdownParser
          color="fg.muted"
          fontSize={{ base: "sm", md: "sm" }}
          liProps={{
            _marker: {
              color: "fg.subtle",
              content: '"-"',
              fontFamily: "mono",
              fontSize: "sm",
              fontWeight: "normal",
              lineHeight: "1.45",
            },
            color: "fg.muted",
            css: {
              "& p": {
                color: "currentColor",
              },
            },
            display: "list-item",
            fontFamily: "body",
            lineHeight: "1.35",
            marginBottom: 0,
            paddingInlineStart: 1,
          }}
          marginBottom="0"
          markdown={value}
          olProps={{
            display: "grid",
            gap: 1,
            listStylePosition: "outside",
            listStyleType: "decimal-leading-zero",
            pl: 2,
          }}
          pProps={{
            color: "fg.muted",
            fontFamily: "body",
            lineHeight: "1.35",
            marginBottom: 0,
            paddingBottom: 0,
          }}
          strongProps={{
            fontWeight: "bold",
          }}
          ulProps={{
            as: "ul",
            display: "grid",
            gap: 1,
            listStylePosition: "outside",
            listStyleType: "decimal-leading-zero",
            pl: 2,
          }}
        />
      ) : (
        <Text color="fg.muted" fontSize="sm">
          {value}
        </Text>
      )}
    </VStack>
  )
}

const ProjectList = ({ items, label }: { items: string[]; label: string }) => {
  if (items.length === 0) {
    return null
  }

  const list = getKeyedItems(items, (item) => item)

  return (
    <VStack alignItems="flex-start" gap={2}>
      <Text
        color="fg"
        fontFamily="mono"
        fontSize="xs"
        fontWeight="600"
        letterSpacing="0.12em"
        lineHeight="1.2"
        textTransform="uppercase"
      >
        {label}
      </Text>
      <HStack
        alignItems="flex-start"
        as={"ul"}
        css={{
          display: "grid",
          gap: 1,
          listStylePosition: "outside",
          listStyleType: "decimal-leading-zero",
          pl: 2,
        }}
        flexWrap="wrap"
        gap={2}
      >
        {list.map(({ item, key }) => (
          <Box
            as="li"
            color={"fg.muted"}
            css={{
              _marker: {
                color: "fg.subtle",
                content: '"-"',
                fontFamily: "mono",
                fontSize: "sm",
                fontWeight: "normal",
                lineHeight: "1.45",
              },
              display: "list-item",
              fontFamily: "body",
              lineHeight: "1.35",
              marginBottom: 0,
              paddingInlineStart: 1,
            }}
            key={key}
          >
            <Text color="fg.muted" fontSize="sm">
              {item}
            </Text>
          </Box>
        ))}
      </HStack>
    </VStack>
  )
}

const SingleHeader: FC<SingleHeaderProps> = ({
  allow_comments = true,
  articlePathPrefix = "/article",
  articleSlug = "",
  author = undefined,
  categories = EMPTY_CATEGORIES,
  client = undefined,
  commentCount = 0,
  date = undefined,
  displayNumber = undefined,
  endDate = undefined,
  headline = undefined,
  industry = undefined,
  likeCount = 3000,
  liked = false,
  methodsUsed = undefined,
  platform = undefined,
  contentLocked = false,
  read_time = undefined,
  readingTime = undefined,
  role = undefined,
  skills = EMPTY_SKILLS,
  startDate = undefined,
  stats = undefined,
  team = undefined,
  thumbnail = undefined,
  title = "",
  titleHighlight = undefined,
  tldr = undefined,
}) => {
  // console.log('skills', skills);
  const today_date = dayjs()
  const article_date = dayjs(date)
  const total_months = Math.abs(today_date.diff(article_date, "month"))
  const duration_years = Math.floor(total_months / 12)
  const duration_months = total_months % 12

  const formatTimeAgo = (years: number, months: number) => {
    if (years === 0 && months === 0) return "This month."
    let parts: string[] = []
    if (years > 0) parts.push(`${years} ${years > 1 ? "Years" : "Year"}`)
    if (months > 0) parts.push(`${months} ${months > 1 ? "Months" : "Month"}`)
    return parts.join(" & ") + " ago."
  }
  const directusAssetsUrl = process.env.DIRECTUS_ASSETS_URL || ""
  const thumbnailImage = thumbnail?.id ? thumbnail : null
  const zoomSrc = thumbnailImage
    ? `${directusAssetsUrl}${thumbnailImage.id}`
    : undefined
  const articleReadingTime = (readingTime ?? read_time)?.trim()
  const visibleReadingTime =
    articleReadingTime && articleReadingTime !== "0" ? articleReadingTime : ""
  const highlightedTitle = getHighlightedText(title, titleHighlight)
  const clientName = getStringValue(client?.name)
  const clientUrl = getStringValue(client?.url)
  const clientValue = clientName ?? clientUrl
  const industryValue = getStringValue(industry)
  const methodsValue = getStringValue(methodsUsed)
  const platformItems = getUniqueItems(getMetadataItems(platform))
  const roleValue = getStringValue(role)
  const teamValue = getStringValue(team)
  const timeline = formatTimeline(startDate, endDate)
  const hasProjectDetails = Boolean(
    roleValue ||
    clientValue ||
    timeline ||
    industryValue ||
    platformItems.length > 0 ||
    teamValue ||
    methodsValue
  )

  return (
    <>
      <VStack
        alignItems="flex-start"
        as={"header"}
        borderLeft={"1px solid {colors.border}"}
        borderRight={"1px solid {colors.border}"}
        className={"work-article-header"}
        gap={0}
        maxW={"8xl"}
        mx="auto"
        pt={{ base: 8, md: 16, lg: 20, xl: 28 }}
        width="100%"
      >
        <HStack
          justifyContent="space-between"
          px={{ base: 8, md: 10, lg: 24, xl: 40 }}
          w="full"
        >
          <CategoryBadgeListArticle
            categories={categories}
            displayNumber={displayNumber}
          />
          {/* TODO: add next and previous article links here, need to fetch those in the loader first */}
          {/* <HStack gap={4} wrap="no-wrap">
            <Link>
              <Icon as={HiOutlineArrowNarrowLeft} />
              <Text>Previous Article</Text>
            </Link>
            <Link>
              <Text>Next Article</Text>
              <Icon as={HiOutlineArrowNarrowRight} />
            </Link>
          </HStack> 
          
          
           <HoverCard.Root size="sm">
            <HoverCard.Trigger asChild>
              <Link href="#">@chakra_ui</Link>
            </HoverCard.Trigger>
            <Portal>
              <HoverCard.Positioner>
                <HoverCard.Content>
                  <HoverCard.Arrow />
                  <Stack gap="4" direction="row">
                    <Avatar.Root>
                      <Avatar.Image src="https://pbs.twimg.com/profile_images/1244925541448286208/rzylUjaf_400x400.jpg" />
                      <Avatar.Fallback name="Chakra UI" />
                    </Avatar.Root>
                    <Stack gap="3">
                      <Stack gap="1">
                        <Text textStyle="sm" fontWeight="semibold">
                          Chakra UI
                        </Text>
                        <Text textStyle="sm" color="fg.muted">
                          The most powerful toolkit for building modern web
                          applications.
                        </Text>
                      </Stack>
                      <HStack color="fg.subtle">
                        <Icon size="sm">
                          <LuChartLine />
                        </Icon>
                        <Text textStyle="xs">2.5M Downloads</Text>
                      </HStack>
                    </Stack>
                  </Stack>
                </HoverCard.Content>
              </HoverCard.Positioner>
            </Portal>
          </HoverCard.Root>
          */}
        </HStack>
        <Box px={{ base: 8, md: 10, lg: 24, xl: 40 }}>
          <Heading
            as="h1"
            color="fg"
            fontWeight="600"
            lineHeight="120%"
            mb={!headline ? 16 : 6}
            mt={6}
            size={{
              base: "3xl",
              md: "3xl",
              lg: "4xl",
              xl: "5xl",
              "2xl": "6xl",
            }}
            width="100%"
          >
            {typeof highlightedTitle === "string" ? (
              highlightedTitle
            ) : (
              <>
                <Text as={"span"} color="fg">
                  {highlightedTitle.before}
                </Text>
                <TextHighlighter action="underline" color="#FF6800">
                  <Box as="span" color="fg.muted">
                    {highlightedTitle.highlight}
                  </Box>
                </TextHighlighter>
                <Text as={"span"} color="fg">
                  {highlightedTitle.after}
                </Text>
              </>
            )}
          </Heading>
        </Box>
        {headline && (
          <Box
            display="box"
            overflow="hidden"
            pb={{ base: 8, md: 8, lg: 8, xl: 8 }}
            px={{ base: 8, md: 10, lg: 24, xl: 40 }}
            w="full"
          >
            <MarkdownParser
              color="fg.muted"
              fontSize="sm"
              headingProps={{
                as: "p",
                color: "fg.muted",
                fontFamily: "body",
                fontSize: "lg",
                fontWeight: "normal",
                lineClamp: "2",
                lineHeight: "1.6",
                marginBottom: 0,
                overflow: "hidden",
                size: "md",
              }}
              marginBottom={0}
              markdown={headline}
              pProps={{
                color: "fg.muted",
                display: "box",
                fontSize: { base: "md", md: "lg" },
                lineClamp: "2",
                lineHeight: "1.6",
                marginBottom: 0,
                overflow: "hidden",
              }}
              strongProps={{
                color: "fg.muted",
                fontSize: "inherit",
              }}
            />
          </Box>
        )}
        <StatsGrid ariaLabel="Project stats" stats={stats} />
        {/* Author and Meta Actions */}
        <Stack
          alignItems={"stretch"}
          borderTop={{
            base: "1px solid transparent",
            md: "1px solid {colors.border}",
          }}
          flexDirection={{ base: "column", lg: "row" }}
          gap={0}
          justifyContent={"space-between"}
          w="full"
        >
          {/* Author */}
          <HStack
            alignItems={"center"}
            borderTop={{
              base: "1px solid {colors.border}",
              md: "1px solid transparent",
            }}
            className="article-author"
            flexShrink={0}
            gap={2}
            px={4}
          >
            <VStack flexShrink={0} py={3}>
              <Avatar.Root size={"md"} variant="subtle">
                <Avatar.Fallback name={author?.name + " " + author?.surname} />
                {author?.image?.id && (
                  <Avatar.Image
                    aria-label={`${author?.name} Image`}
                    src={`${process.env.DIRECTUS_ASSETS_URL || ""}${author.image.id}?quality=100&width=50`}
                  />
                )}
              </Avatar.Root>
            </VStack>
            <VStack alignItems={"flex-start"} flexShrink={1} gap="0">
              <Heading
                _hover={{
                  color: "primary.fg",
                  textDecoration: "underline",
                }}
                as={"h5"}
                asChild
                color="fg"
                fontFamily="body"
                size="md"
              >
                <NextLink
                  color="fg"
                  href={"/cv"}
                  transitionTypes={["nav-forward"]}
                >
                  {author?.name + " " + author?.surname}
                </NextLink>
              </Heading>
              <Text
                as={"p"}
                color="fg.muted"
                flexWrap={"wrap"}
                fontFamily="mono"
                fontSize="sm"
              >
                <Text as="span">{article_date.format("DD MMMM YYYY")}</Text>
                {" - "}
                <Text as="span">
                  {formatTimeAgo(duration_years, duration_months)}
                </Text>
              </Text>
            </VStack>
          </HStack>

          {/* Meta Action Buttons */}
          <ArticleMetaActions
            allow_comments={allow_comments}
            articlePathPrefix={articlePathPrefix}
            articleSlug={articleSlug}
            commentCount={commentCount}
            likeCount={likeCount}
            liked={liked}
            readingTime={visibleReadingTime}
          />
        </Stack>
        {thumbnailImage ? (
          <Box
            aspectRatio="16 / 9"
            data-print-thumbnail-frame=""
            overflow={"hidden"}
            position="relative"
            width={"100%"}
          >
            <Box
              data-print-thumbnail-inner=""
              height={"full"}
              left={"50%"}
              overflow={"hidden"}
              position={"relative"}
              transform={"translateX(-50%)"}
              width={"full"}
            >
              <Inner
                default="none"
                name={`work-article-image-${articleSlug}`}
                share="work-image-morph"
              >
                <ClientZoom
                  zoomImg={
                    zoomSrc
                      ? {
                          alt: title ?? "Full image thumbnail",
                          src: zoomSrc,
                        }
                      : undefined
                  }
                >
                  <Img
                    alt={
                      thumbnailImage.description == undefined ||
                      thumbnailImage.description == null
                        ? "Thumbnail Image"
                        : thumbnailImage.description
                    }
                    breakpointWidths={{
                      base: "390",
                      sm: "640",
                      md: "960",
                      lg: "1280",
                      xl: "1536",
                      "2xl": "1920",
                    }}
                    // urlWidth={1480}
                    crop={{ enabled: false }}
                    fetchPriority="high"
                    height={"100%"}
                    noContainer
                    objectFit="contain"
                    originalHeight={thumbnailImage.height}
                    originalWidth={thumbnailImage.width}
                    priority
                    sizes="(max-width: 1536px) 100vw, 1536px"
                    src={thumbnailImage.id}
                    transform={{ fit: "contain", format: "auto", quality: 88 }}
                    width={"100%"}
                  />
                </ClientZoom>
              </Inner>
            </Box>
          </Box>
        ) : null}

        {/* Skill Author and Meta Actions */}
        <Stack
          alignItems={"stretch"}
          borderTop={"1px solid {colors.border}"}
          flexDirection={{ base: "column", lg: "row" }}
          gap={0}
          justifyContent={"space-between"}
          w="full"
        >
          {/* Skills  */}
          {skills && (
            <HStack
              borderTop={{
                base: "1px solid {colors.border}",
                lg: "1px solid transparent",
              }}
              className="article-tags"
              flexGrow={1}
            >
              <HStack
                _dark={{ color: "gray.500" }}
                color="gray.400"
                columnGap={2}
                mb={2}
                pt={2}
                px={4}
                rowGap={0}
                wrap="wrap"
              >
                <Text fontSize="sm">
                  <TagsIcon boxSize="16px" stroke="currentColor" />{" "}
                </Text>
                {skills.map((skill, index) => (
                  <Box key={skill.id ?? skill.slug}>
                    <Link
                      _hover={{
                        color: "primary.fg",
                      }}
                      asChild
                      color="fg.muted"
                    >
                      <NextLink
                        href={skillsURL + skill.slug}
                        transitionTypes={["nav-forward"]}
                      >
                        <Text
                          as="span"
                          fontFamily="mono"
                          fontSize="sm"
                          lineHeight="1"
                          py={0}
                        >
                          {skill.name}
                        </Text>
                      </NextLink>
                    </Link>
                    {index < skills.length - 1 && <Text as="span">, </Text>}
                  </Box>
                ))}
              </HStack>
            </HStack>
          )}
        </Stack>
        {!contentLocked && (
          <Stack
            alignItems="stretch"
            className="article-meta-bottom"
            flexDirection={{ base: "column", lg: "row" }}
            gap={0}
            justifyContent="space-between"
            w="full"
          >
            <VStack
              alignItems="flex-start"
              borderTop={"1px solid {colors.border}"}
              gap={6}
              maxW={{ base: "100%", lg: tldr ? "40%" : "100%" }}
              px={{ base: 8, md: 10, lg: 12, xl: 12 }}
              py={{ base: 8, md: 10, lg: 12, xl: 12 }}
              w="full"
            >
              {hasProjectDetails && (
                <>
                  <SimpleGrid
                    columnGap={4}
                    columns={{ base: 1, md: 2, lg: 2 }}
                    rowGap={6}
                    w="full"
                  >
                    <ProjectDetailItem label="Role" value={roleValue} />
                    <ProjectDetailItem label="Timeline" value={timeline} />
                    <ProjectDetailItem
                      label="Industry"
                      markdown
                      value={industryValue}
                    />
                    {clientValue && (
                      <ProjectDetailItem
                        href={clientUrl}
                        label="Client"
                        value={clientValue}
                      />
                    )}
                    <ProjectList items={platformItems} label="Platform" />
                    <ProjectDetailItem
                      label="Methods"
                      markdown
                      value={methodsValue}
                    />
                    <ProjectDetailItem
                      label="Team"
                      markdown
                      value={teamValue}
                    />
                  </SimpleGrid>
                </>
              )}
            </VStack>
            {tldr && (
              <VStack
                alignItems="flex-start"
                borderLeft={{
                  base: "0",
                  lg: "1px solid {colors.border}",
                }}
                borderTop={"1px solid {colors.border}"}
                className="tldr"
                gap={0}
                maxW={{ base: "100%", lg: "60%" }}
                w="full"
              >
                <VStack
                  gap={0}
                  justifyContent="space-between"
                  px={{ base: 8, md: 10, lg: 12, xl: 12 }}
                  py={{ base: 8, md: 10, lg: 12, xl: 12 }}
                >
                  <Heading pb={{ base: 3 }} w="full">
                    TL;DR
                  </Heading>
                  <MarkdownParser
                    color="fg.muted"
                    fontSize={{ base: "sm", md: "md" }}
                    liProps={{}}
                    marginBottom="0"
                    markdown={tldr}
                    pProps={{
                      fontSize: "inherit",
                      fontWeight: "inherit",
                      lineHeight: "inherit",
                      marginBottom: 0,
                      paddingBottom: 0,
                    }}
                    strongProps={{
                      color: "inherit",
                      fontSize: "inherit",
                      fontWeight: "bold",
                    }}
                  />
                </VStack>
              </VStack>
            )}
          </Stack>
        )}
      </VStack>
    </>
  )
}

export default SingleHeader
